import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useSubscription } from './SubscriptionContext';

const ChatContext = createContext();

/**
 * Chat Context Provider
 * Manages chat messages and rooms
 */
export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const { canAccessPremium } = useSubscription();
  const [messages, setMessages] = useState({});
  const [activeRoom, setActiveRoom] = useState(null);

  // Load messages from localStorage
  useEffect(() => {
    const storedMessages = localStorage.getItem('rocketverse_chat_messages');
    if (storedMessages) {
      setMessages(JSON.parse(storedMessages));
    }
  }, []);

  /**
   * Get or create chat room
   */
  const getRoomMessages = (roomId) => {
    return messages[roomId] || [];
  };

  /**
   * Send a message to a room
   */
  const sendMessage = (roomId, messageText) => {
    if (!user) {
      return { success: false, message: 'Please login first' };
    }

    if (!canAccessPremium()) {
      return { success: false, message: 'Premium subscription required for chat' };
    }

    const newMessage = {
      id: Date.now().toString(),
      roomId: roomId,
      userId: user.id,
      userName: user.name || user.email,
      text: messageText,
      timestamp: new Date().toISOString()
    };

    const updatedMessages = {
      ...messages,
      [roomId]: [...(messages[roomId] || []), newMessage]
    };

    setMessages(updatedMessages);
    localStorage.setItem('rocketverse_chat_messages', JSON.stringify(updatedMessages));

    return { success: true, message: newMessage };
  };

  /**
   * Create or join a room
   */
  const joinRoom = (roomId) => {
    setActiveRoom(roomId);
    if (!messages[roomId]) {
      const updatedMessages = {
        ...messages,
        [roomId]: []
      };
      setMessages(updatedMessages);
      localStorage.setItem('rocketverse_chat_messages', JSON.stringify(updatedMessages));
    }
  };

  /**
   * Leave current room
   */
  const leaveRoom = () => {
    setActiveRoom(null);
  };

  const value = {
    messages,
    activeRoom,
    getRoomMessages,
    sendMessage,
    joinRoom,
    leaveRoom,
    canAccessChat: canAccessPremium()
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

/**
 * Custom hook to use Chat Context
 */
export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
